import { bigIntParser } from "../utils/functions/bigIntParser";
import { endpointGenerator } from "../utils/functions/endpointGenerator";
import { type Execution } from "../utils/functions/execution";
import { paginateEndpoint } from "../utils/functions/paginateEndpoint";
import {
    type AllChainsResponse,
    type AllChainsStatusResponse,
    type BlockHeightsResponse,
    type BlockResponse,
    type GasPricesResponse,
    type GetBlockHeightsQueryParamOpts,
    type GetGasPricesQueryParamOpts,
    type GetLogEventsByAddressQueryParamOpts,
    type GetLogEventsByTopicHashQueryParamOpts,
    type GetLogsQueryParamOpts,
    type GetLogsResponse,
    type LogEventsByAddressResponse,
    type LogEventsByTopicHashResponse,
    type ResolvedAddress,
} from "../utils/types/BaseService.types";
import {
    type Chain,
    type GoldRushResponse,
} from "../utils/types/Generic.types";

/**
 * Base API
 *
 */
export class BaseService {
    constructor(private execution: Execution) {}

    /**
     *
     * Commonly used to fetch and render a single block for a block explorer.
     *
     * **Credit Cost**: 1 per call
     *
     * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
     * @param {string} blockHeight - The block height or `latest` for the latest block available.
     *
     */
    public async getBlock(
        chainName: Chain,
        blockHeight: string
    ): Promise<GoldRushResponse<BlockResponse>> {
        const endpoint = endpointGenerator(
            `${chainName}/block_v2/${blockHeight}`,
            []
        );

        const parseData = (data: GoldRushResponse<BlockHeightsResponse>) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((blockItem) => ({
                          ...blockItem,
                          signed_at: blockItem.signed_at
                              ? new Date(blockItem.signed_at)
                              : null,
                      }))
                    : null;
            }

            return data;
        };

        return await this.execution.execute<BlockHeightsResponse>(
            endpoint,
            parseData
        );
    }

    /**
     *
     * Commonly used to resolve ENS, RNS and Unstoppable Domains addresses. Only supports the resolution of a registered domain to an address.
     *
     * **Credit Cost**: 1 per call
     *
     * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
     * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     *
     */
    public async getResolvedAddress(
        chainName: Chain,
        walletAddress: string
    ): Promise<GoldRushResponse<ResolvedAddress>> {
        const endpoint = endpointGenerator(
            `${chainName}/address/${walletAddress}/resolve_address`,
            []
        );

        const parseData = (data: GoldRushResponse<ResolvedAddress>) => {
            return data;
        };

        return await this.execution.execute<ResolvedAddress>(
            endpoint,
            parseData
        );
    }

    /**
     *
     * Commonly used to get all the block heights within a particular date range. Useful for rendering a display where you sort blocks by day.
     *
     * **Credit Cost**: 1 per call
     *
     * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
     * @param {string} startDate - The start date in YYYY-MM-DD format.
     * @param {string | "latest"} endDate - The end date in YYYY-MM-DD format. Also accepts "latest" for the latest block height
     * @param {GetBlockHeightsQueryParamOpts} queryParamOpts
     *   - `pageSize`: Number of items per page. Omitting this parameter defaults to 100.
     *   - `pageNumber`: 0-indexed page number to begin pagination.
     *
     */
    public async *getBlockHeights(
        chainName: Chain,
        startDate: string,
        endDate: string | "latest",
        queryParamOpts?: GetBlockHeightsQueryParamOpts
    ): AsyncIterable<GoldRushResponse<BlockHeightsResponse>> {
        const endpoint = endpointGenerator(
            `${chainName}/block_v2/${startDate}/${endDate}`,
            [
                {
                    key: "page-size",
                    value: queryParamOpts?.pageSize,
                },
                {
                    key: "page-number",
                    value: queryParamOpts?.pageNumber,
                },
            ]
        );

        const parseData = (data: GoldRushResponse<BlockHeightsResponse>) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((blockItem) => ({
                          ...blockItem,
                          signed_at: blockItem.signed_at
                              ? new Date(blockItem.signed_at)
                              : null,
                      }))
                    : null;
            }

            return data;
        };

        for await (const data of paginateEndpoint<BlockHeightsResponse>(
            endpoint,
            this.execution,
            parseData,
            "pagination"
        )) {
            yield data;
        }
    }

    /**
     *
     * Commonly used to get all the block heights within a particular date range. Useful for rendering a display where you sort blocks by day.
     *
     * **Credit Cost**: 1 per call
     *
     * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
     * @param {string} startDate - The start date in YYYY-MM-DD format.
     * @param {string | "latest"} endDate - The end date in YYYY-MM-DD format. Also accepts "latest" for the latest block height
     * @param {GetBlockHeightsQueryParamOpts} queryParamOpts
     *   - `pageSize`: Number of items per page. Omitting this parameter defaults to 100.
     *   - `pageNumber`: 0-indexed page number to begin pagination.
     *
     */
    public async getBlockHeightsByPage(
        chainName: Chain,
        startDate: string,
        endDate: string | "latest",
        queryParamOpts?: GetBlockHeightsQueryParamOpts
    ): Promise<GoldRushResponse<BlockHeightsResponse>> {
        const endpoint = endpointGenerator(
            `${chainName}/block_v2/${startDate}/${endDate}`,
            [
                {
                    key: "page-size",
                    value: queryParamOpts?.pageSize,
                },
                {
                    key: "page-number",
                    value: queryParamOpts?.pageNumber,
                },
            ]
        );

        const parseData = (data: GoldRushResponse<BlockHeightsResponse>) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((blockItem) => ({
                          ...blockItem,
                          signed_at: blockItem.signed_at
                              ? new Date(blockItem.signed_at)
                              : null,
                      }))
                    : null;
            }

            return data;
        };

        return await this.execution.execute<BlockHeightsResponse>(
            endpoint,
            parseData
        );
    }

    /**
     *
     * Commonly used to get all the event logs of the latest block, or for a range of blocks. Includes sender contract metadata as well as decoded logs.
     *
     * **Credit Cost**: 0.01 per item
     *
     * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
     * @param {GetLogsQueryParamOpts} queryParamOpts
     *   - `startingBlock`: The first block to retrieve log events with. Accepts decimals, hexadecimals, or the strings `earliest` and `latest`.
     *   - `endingBlock`: The last block to retrieve log events with. Accepts decimals, hexadecimals, or the strings `earliest` and `latest`.
     *   - `address`: The address of the log events sender contract.
     *   - `topics`: The topic hash(es) to retrieve logs with.
     *   - `blockHash`: The block hash to retrieve logs for.
     *   - `skipDecode`: Omit decoded log events.
     *
     */
    public async getLogs(
        chainName: Chain,
        queryParamOpts?: GetLogsQueryParamOpts
    ): Promise<GoldRushResponse<GetLogsResponse>> {
        const endpoint = endpointGenerator(`${chainName}/events`, [
            {
                key: "starting-block",
                value: queryParamOpts?.startingBlock,
            },
            {
                key: "ending-block",
                value: queryParamOpts?.endingBlock,
            },
            {
                key: "address",
                value: queryParamOpts?.address,
            },
            {
                key: "topics",
                value: queryParamOpts?.topics,
            },
            {
                key: "block-hash",
                value: queryParamOpts?.blockHash,
            },
            {
                key: "skip-decode",
                value: queryParamOpts?.skipDecode,
            },
        ]);

        const parseData = (data: GoldRushResponse<GetLogsResponse>) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((logItem) => ({
                          ...logItem,
                          block_signed_at: logItem.block_signed_at
                              ? new Date(logItem.block_signed_at)
                              : null,
                      }))
                    : null;
            }

            return data;
        };

        return await this.execution.execute<GetLogsResponse>(
            endpoint,
            parseData
        );
    }

    /**
     *
     * Commonly used to get all the event logs emitted from a particular contract address. Useful for building dashboards that examine on-chain interactions.
     *
     * **Credit Cost**: 0.01 per item
     *
     * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
     * @param {string} contractAddress - The requested contract address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     * @param {GetLogEventsByAddressQueryParamOpts} queryParamOpts
     *   - `startingBlock`: The first block to retrieve log events with. Accepts decimals, hexadecimals, or the strings `earliest` and `latest`.
     *   - `endingBlock`: The last block to retrieve log events with. Accepts decimals, hexadecimals, or the strings `earliest` and `latest`.
     *   - `pageSize`: Number of items per page. Omitting this parameter defaults to 100.
     *   - `pageNumber`: 0-indexed page number to begin pagination.
     *
     */
    public async *getLogEventsByAddress(
        chainName: Chain,
        contractAddress: string,
        queryParamOpts?: GetLogEventsByAddressQueryParamOpts
    ): AsyncIterable<GoldRushResponse<LogEventsByAddressResponse>> {
        const endpoint = endpointGenerator(
            `${chainName}/events/address/${contractAddress}`,
            [
                {
                    key: "starting-block",
                    value: queryParamOpts?.startingBlock,
                },
                {
                    key: "ending-block",
                    value: queryParamOpts?.endingBlock,
                },
                {
                    key: "page-size",
                    value: queryParamOpts?.pageSize,
                },
                {
                    key: "page-number",
                    value: queryParamOpts?.pageNumber,
                },
            ]
        );

        const parseData = (
            data: GoldRushResponse<LogEventsByAddressResponse>
        ) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((logItem) => ({
                          ...logItem,
                          block_signed_at: logItem.block_signed_at
                              ? new Date(logItem.block_signed_at)
                              : null,
                      }))
                    : null;
            }

            return data;
        };

        for await (const data of paginateEndpoint<LogEventsByAddressResponse>(
            endpoint,
            this.execution,
            parseData,
            "pagination"
        )) {
            yield data;
        }
    }

    /**
     *
     * Commonly used to get all the event logs emitted from a particular contract address. Useful for building dashboards that examine on-chain interactions.
     *
     * **Credit Cost**: 0.01 per item
     *
     * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
     * @param {string} contractAddress - The requested contract address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     * @param {GetLogEventsByAddressQueryParamOpts} queryParamOpts
     *   - `startingBlock`: The first block to retrieve log events with. Accepts decimals, hexadecimals, or the strings `earliest` and `latest`.
     *   - `endingBlock`: The last block to retrieve log events with. Accepts decimals, hexadecimals, or the strings `earliest` and `latest`.
     *   - `pageSize`: Number of items per page. Omitting this parameter defaults to 100.
     *   - `pageNumber`: 0-indexed page number to begin pagination.
     *
     */
    public async getLogEventsByAddressByPage(
        chainName: Chain,
        contractAddress: string,
        queryParamOpts?: GetLogEventsByAddressQueryParamOpts
    ): Promise<GoldRushResponse<LogEventsByAddressResponse>> {
        const endpoint = endpointGenerator(
            `${chainName}/events/address/${contractAddress}`,
            [
                {
                    key: "starting-block",
                    value: queryParamOpts?.startingBlock,
                },
                {
                    key: "ending-block",
                    value: queryParamOpts?.endingBlock,
                },
                {
                    key: "page-size",
                    value: queryParamOpts?.pageSize,
                },
                {
                    key: "page-number",
                    value: queryParamOpts?.pageNumber,
                },
            ]
        );

        const parseData = (
            data: GoldRushResponse<LogEventsByAddressResponse>
        ) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((logItem) => ({
                          ...logItem,
                          block_signed_at: logItem.block_signed_at
                              ? new Date(logItem.block_signed_at)
                              : null,
                      }))
                    : null;
            }

            return data;
        };

        return await this.execution.execute<LogEventsByAddressResponse>(
            endpoint,
            parseData
        );
    }

    /**
     *
     * Commonly used to get all event logs of the same topic hash across all contracts within a particular chain. Useful for cross-sectional analysis of event logs that are emitted on-chain.
     *
     * **Credit Cost**: 0.01 per item
     *
     * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
     * @param {string} topicHash - The endpoint will return event logs that contain this topic hash.
     * @param {GetLogEventsByTopicHashQueryParamOpts} queryParamOpts
     *   - `startingBlock`: The first block to retrieve log events with. Accepts decimals, hexadecimals, or the strings `earliest` and `latest`.
     *   - `endingBlock`: The last block to retrieve log events with. Accepts decimals, hexadecimals, or the strings `earliest` and `latest`.
     *   - `secondaryTopics`: Additional topic hash(es) to filter on - padded & unpadded address fields are supported. Separate multiple topics with a comma.
     *   - `pageSize`: Number of items per page. Omitting this parameter defaults to 100.
     *   - `pageNumber`: 0-indexed page number to begin pagination.
     *
     */
    public async *getLogEventsByTopicHash(
        chainName: Chain,
        topicHash: string,
        queryParamOpts?: GetLogEventsByTopicHashQueryParamOpts
    ): AsyncIterable<GoldRushResponse<LogEventsByTopicHashResponse>> {
        const endpoint = endpointGenerator(
            `${chainName}/events/topics/${topicHash}`,
            [
                {
                    key: "starting-block",
                    value: queryParamOpts?.startingBlock,
                },
                {
                    key: "ending-block",
                    value: queryParamOpts?.endingBlock,
                },
                {
                    key: "secondary-topics",
                    value: queryParamOpts?.secondaryTopics,
                },
                {
                    key: "page-size",
                    value: queryParamOpts?.pageSize,
                },
                {
                    key: "page-number",
                    value: queryParamOpts?.pageNumber,
                },
            ]
        );

        const parseData = (
            data: GoldRushResponse<LogEventsByTopicHashResponse>
        ) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((logItem) => ({
                          ...logItem,
                          block_signed_at: logItem.block_signed_at
                              ? new Date(logItem.block_signed_at)
                              : null,
                      }))
                    : null;
            }

            return data;
        };

        for await (const data of paginateEndpoint<LogEventsByTopicHashResponse>(
            endpoint,
            this.execution,
            parseData,
            "pagination"
        )) {
            yield data;
        }
    }

    /**
     *
     * Commonly used to get all event logs of the same topic hash across all contracts within a particular chain. Useful for cross-sectional analysis of event logs that are emitted on-chain.
     *
     * **Credit Cost**: 0.01 per item
     *
     * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
     * @param {string} topicHash - The endpoint will return event logs that contain this topic hash.
     * @param {GetLogEventsByTopicHashQueryParamOpts} queryParamOpts
     *   - `startingBlock`: The first block to retrieve log events with. Accepts decimals, hexadecimals, or the strings `earliest` and `latest`.
     *   - `endingBlock`: The last block to retrieve log events with. Accepts decimals, hexadecimals, or the strings `earliest` and `latest`.
     *   - `secondaryTopics`: Additional topic hash(es) to filter on - padded & unpadded address fields are supported. Separate multiple topics with a comma.
     *   - `pageSize`: Number of items per page. Omitting this parameter defaults to 100.
     *   - `pageNumber`: 0-indexed page number to begin pagination.
     *
     */
    public async getLogEventsByTopicHashByPage(
        chainName: Chain,
        topicHash: string,
        queryParamOpts?: GetLogEventsByTopicHashQueryParamOpts
    ): Promise<GoldRushResponse<LogEventsByTopicHashResponse>> {
        const endpoint = endpointGenerator(
            `${chainName}/events/topics/${topicHash}`,
            [
                {
                    key: "starting-block",
                    value: queryParamOpts?.startingBlock,
                },
                {
                    key: "ending-block",
                    value: queryParamOpts?.endingBlock,
                },
                {
                    key: "secondary-topics",
                    value: queryParamOpts?.secondaryTopics,
                },
                {
                    key: "page-size",
                    value: queryParamOpts?.pageSize,
                },
                {
                    key: "page-number",
                    value: queryParamOpts?.pageNumber,
                },
            ]
        );

        const parseData = (
            data: GoldRushResponse<LogEventsByTopicHashResponse>
        ) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((logItem) => ({
                          ...logItem,
                          block_signed_at: logItem.block_signed_at
                              ? new Date(logItem.block_signed_at)
                              : null,
                      }))
                    : null;
            }

            return data;
        };

        return await this.execution.execute<LogEventsByTopicHashResponse>(
            endpoint,
            parseData
        );
    }

    /**
     *
     * Commonly used to build internal dashboards for all supported chains on Covalent.
     *
     * **Credit Cost**: 0.01 per call
     *
     */
    public async getAllChains(): Promise<GoldRushResponse<AllChainsResponse>> {
        const endpoint = endpointGenerator(`chains`);

        const parseData = (data: GoldRushResponse<AllChainsResponse>) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((chainItem) => ({
                          ...chainItem,
                          chain_id: chainItem.chain_id
                              ? +chainItem.chain_id
                              : null,
                      }))
                    : null;
            }

            return data;
        };

        return await this.execution.execute<AllChainsResponse>(
            endpoint,
            parseData
        );
    }

    /**
     *
     * Commonly used to build internal status dashboards of all supported chains.
     *
     * **Credit Cost**: 1 per call
     *
     */
    public async getAllChainStatus(): Promise<
        GoldRushResponse<AllChainsStatusResponse>
    > {
        const endpoint = endpointGenerator(`chains/status`);

        const parseData = (data: GoldRushResponse<AllChainsStatusResponse>) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((chainItem) => ({
                          ...chainItem,
                          chain_id: chainItem.chain_id
                              ? +chainItem.chain_id
                              : null,
                      }))
                    : null;
            }

            return data;
        };

        return await this.execution.execute<AllChainsStatusResponse>(
            endpoint,
            parseData
        );
    }

    /**
     *
     * Get real-time gas estimates for different transaction speeds on a specific network, enabling users to optimize transaction costs and confirmation times.
     *
     * **Credit Cost**: 1 per call
     *
     * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
     * @param {string} eventType - The desired event type to retrieve gas prices for. Supports `erc20` transfer events, `uniswapv3` swap events and `nativetokens` transfers.
     * @param {GetGasPricesQueryParamOpts} queryParamOpts
     *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
     *
     */
    public async getGasPrices(
        chainName: Chain,
        eventType: "erc20" | "nativetokens" | "uniswapv3",
        queryParamOpts?: GetGasPricesQueryParamOpts
    ): Promise<GoldRushResponse<GasPricesResponse>> {
        const endpoint = endpointGenerator(
            `${chainName}/event/${eventType}/gas_prices`,
            [
                {
                    key: "quote-currency",
                    value: queryParamOpts?.quoteCurrency,
                },
            ]
        );

        const parseData = (data: GoldRushResponse<GasPricesResponse>) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.base_fee = bigIntParser(data.data.base_fee);
            }

            return data;
        };

        return await this.execution.execute<GasPricesResponse>(
            endpoint,
            parseData
        );
    }
}
